import React, { useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import { Download, FileText, FileArchive, Thermometer, SprayCan, ListChecks, ClipboardCheck, Activity, ShoppingCart, Loader2, Timer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const REPORTS = [
  { type: "temperature", label: "Temperature report", icon: Thermometer },
  { type: "cleaning", label: "Cleaning report", icon: SprayCan },
  { type: "prep", label: "Prep report", icon: ListChecks },
  { type: "checklist", label: "Checklist report", icon: ClipboardCheck },
  { type: "activity", label: "Staff activity report", icon: Activity },
  { type: "orders", label: "Ordering history", icon: ShoppingCart },
  { type: "sous_vide", label: "Sous-vide records", icon: Timer, module: "haccp" },
];

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
function fmtCell(c) {
  const s = String(c);
  if (ISO_RE.test(s)) return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return s;
}

export default function Reporting() {
  const { moduleEnabled } = useAuth();
  const [active, setActive] = useState("temperature");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const pad2 = (n) => String(n).padStart(2, "0");
  const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const today = toISO(new Date());
  const [packFrom, setPackFrom] = useState(today);
  const [packTo, setPackTo] = useState(today);

  const isoDaysAgo = (days) => { const s = new Date(); s.setDate(s.getDate() - (days - 1)); return toISO(s); };
  const isoMonthsAgo = (m) => { const s = new Date(); const day = s.getDate(); s.setMonth(s.getMonth() - m); if (s.getDate() !== day) s.setDate(0); return toISO(s); };
  const PACK_PRESETS = [
    { label: "Today", from: today, to: today },
    { label: "Last 7 days", from: isoDaysAgo(7), to: today },
    { label: "Last 30 days", from: isoDaysAgo(30), to: today },
    { label: "Last 3 months", from: isoMonthsAgo(3), to: today },
    { label: "Last 6 months", from: isoMonthsAgo(6), to: today },
    { label: "Last 12 months", from: isoMonthsAgo(12), to: today },
  ];
  const applyPreset = (p) => { setPackFrom(p.from); setPackTo(p.to); };

  const qs = () => {
    const p = [];
    if (from) p.push(`from=${from}`);
    if (to) p.push(`to=${to}`);
    return p.length ? "?" + p.join("&") : "";
  };

  const load = async (type) => {
    setActive(type); setLoading(true);
    try { setData((await api.get(`/reports/${type}${qs()}`)).data); } catch { setData({ headers: [], rows: [], count: 0 }); } finally { setLoading(false); }
  };
  React.useEffect(() => { load(active); /* eslint-disable-next-line */ }, [from, to]);

  const token = () => localStorage.getItem("cc_token");
  const withDates = (base) => { const s = qs(); return `${base}${s ? s + "&" : "?"}auth=${token()}`; };
  const downloadCsv = () => window.open(withDates(`${API}/reports/${active}/csv`), "_blank");
  const downloadPdf = () => window.open(withDates(`${API}/reports/${active}/pdf`), "_blank");
  const downloadPack = () => {
    if (!packFrom || !packTo) { toast.error("Please choose both a From and a To date for the EHO pack."); return; }
    if (packFrom > packTo) { toast.error("From date must be on or before the To date."); return; }
    const days = Math.round((new Date(packTo) - new Date(packFrom)) / 86400000) + 1;
    if (days > 366) { toast.error("The reporting period is too large. The maximum is 366 days (about 12 months) — please narrow the range."); return; }
    window.open(`${API}/daily-pack/pdf?from=${packFrom}&to=${packTo}&auth=${token()}`, "_blank");
  };

  return (
    <div>
      <PageHeader title="Reporting" subtitle="Accountability records, filterable and exportable to CSV / PDF." testid="reporting-header" />

      <div className="cc-card p-4 mb-6" data-testid="daily-pack-card">
        <div className="flex items-center gap-2 text-slate-700 font-semibold"><FileArchive size={18} className="text-brand" /> EHO food-safety pack</div>
        <p className="text-xs text-slate-400 mt-1">Bundles temperatures, cleaning, checklists, prep &amp; sous-vide batches for the selected period into one signed PDF — ideal for EHO inspections.</p>
        <div className="flex flex-wrap items-end gap-4 mt-3">
          <div><Label className="text-xs font-semibold text-slate-600">From</Label><div className="mt-1.5"><DatePicker value={packFrom} onChange={setPackFrom} placeholder="Start date" testid="pack-from" className="w-44" /></div></div>
          <div><Label className="text-xs font-semibold text-slate-600">To</Label><div className="mt-1.5"><DatePicker value={packTo} onChange={setPackTo} placeholder="End date" testid="pack-to" className="w-44" /></div></div>
          <Button onClick={downloadPack} className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="download-pack"><Download size={16} className="mr-1" /> Download EHO pack (PDF)</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="pack-presets">
          {PACK_PRESETS.map((p) => (
            <button key={p.label} onClick={() => applyPreset(p)} data-testid={`pack-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${packFrom === p.from && packTo === p.to ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {REPORTS.filter((r) => !r.module || moduleEnabled(r.module)).map((r) => (
          <button key={r.type} onClick={() => load(r.type)} data-testid={`report-${r.type}`}
            className={`cc-card p-4 flex items-center gap-3 text-left transition-all ${active === r.type ? "border-brand ring-1 ring-brand" : "hover:border-slate-300"}`}>
            <div className="h-10 w-10 rounded-xl bg-brand-light flex items-center justify-center"><r.icon size={18} className="text-brand-dark" /></div>
            <span className="font-semibold text-sm text-slate-700">{r.label}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div className="flex items-end gap-3">
          <div><Label className="text-xs font-semibold text-slate-600">From</Label><div className="mt-1.5"><DatePicker value={from} onChange={setFrom} placeholder="Start date" testid="filter-from" className="w-40" /></div></div>
          <div><Label className="text-xs font-semibold text-slate-600">To</Label><div className="mt-1.5"><DatePicker value={to} onChange={setTo} placeholder="End date" testid="filter-to" className="w-40" /></div></div>
          {(from || to) && <Button variant="ghost" className="text-slate-500" data-testid="clear-dates" onClick={() => { setFrom(""); setTo(""); }}>Clear</Button>}
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadPdf} variant="outline" className="rounded-xl border-slate-300" data-testid="download-pdf"><FileText size={16} className="mr-1" /> PDF</Button>
          <Button onClick={downloadCsv} className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="download-csv"><Download size={16} className="mr-1" /> Export CSV</Button>
        </div>
      </div>

      <h2 className={`font-display font-bold text-slate-800 mb-3 ${active === "sous_vide" ? "" : "capitalize"}`}>{active === "sous_vide" ? "Sous-vide records" : `${active} report`} {data && <span className="text-sm font-normal text-slate-400">({data.count} rows)</span>}</h2>
      {active === "sous_vide" && <p className="text-xs text-slate-500 mb-3" data-testid="sv-report-note">Manual spot readings against each site's own approved process. “Required records complete” means the site-configured stages were recorded within configured limits; it is not an independent food-safety validation by CaterCTRL. Original deviations stay visible after review.</p>}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand" /></div> :
        data && (
          <div className="cc-card overflow-x-auto" data-testid="report-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-100">{data.headers.map((h) => <th key={h} className="text-left px-4 py-3 font-semibold text-slate-600 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.length === 0 && <tr><td colSpan={data.headers.length} className="px-4 py-8 text-center text-slate-400">No data for this period.</td></tr>}
                {data.rows.slice(0, 300).map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50">{row.map((c, j) => <td key={j} className={`px-4 py-2.5 text-slate-600 ${active === "sous_vide" ? "align-top min-w-[12rem] whitespace-pre-line" : "whitespace-nowrap"}`}>{active === "sous_vide" && !ISO_RE.test(String(c)) ? String(c).replace(/; /g, "\n") : fmtCell(c)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
